import React from 'react';
import { DeepMap } from 'react-hook-form';
import deepEqual from 'fast-deep-equal';
import { SelectOptionElement, useDidUpdateEffect } from '@odin-labs/components';
import {
  ChangeType,
  HidProxyInstance,
  type JobsiteBadgeTemplateInput,
  JobsiteUpdateInput,
} from 'apollo/generated/client-operations';
import { PrintIcon } from 'components/icons';
import {
  FormInputTypes,
  FormRepeaterArgs,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import {
  BadgeTemplateOptionElement,
  EditJobsiteConfigurationFormData,
  HidFargoSelectOptionElement,
  JobsitePrinter,
} from 'containers/jobsiteConfiguration/types';
import {
  cn,
  ensureNonUndefinedFields,
  getTempId,
  getSelectOptionsFromStringEnum,
} from 'utils';
import { Editable } from 'types';
import { Jobsite } from './types';

export type HidFargoConnectSectionInputsArgs = {
  organizationOptions: HidFargoSelectOptionElement[];
  locationOptions: HidFargoSelectOptionElement[];
  printerOptions: HidFargoSelectOptionElement[];
  proxyInstanceOptions: SelectOptionElement<HidProxyInstance>[];
  selectedProxyInstance?: SelectOptionElement;
  badgeTemplateOptions: BadgeTemplateOptionElement[];
  defaultBadgeTemplateOptions: BadgeTemplateOptionElement[];
  temporaryBadgeTemplateOptions: BadgeTemplateOptionElement[];
  defaultTemporaryBadgeTemplateOptions: BadgeTemplateOptionElement[];
};

export type HidFargoConnectSectionInputsHookArgs = Omit<
  HidFargoConnectSectionInputsArgs,
  | 'defaultBadgeTemplateOptions'
  | 'defaultTemporaryBadgeTemplateOptions'
  | 'assignedPrinterOptions'
>;

export const hidProxyInstanceSelectOptions =
  getSelectOptionsFromStringEnum(HidProxyInstance);

export const buildJobsiteBadgeTemplateInputs = (args: {
  permanentIds: string[] | null | undefined;
  defaultPermanentId: string | null | undefined;
  temporaryIds: string[] | null | undefined;
  defaultTemporaryId: string | null | undefined;
}): JobsiteBadgeTemplateInput[] | null => {
  const { permanentIds, defaultPermanentId, temporaryIds, defaultTemporaryId } =
    args;
  const permanentInputs =
    permanentIds?.map((badgeTemplateId) => ({
      badgeTemplateId,
      isDefault: badgeTemplateId === defaultPermanentId,
    })) ?? [];
  const temporaryInputs =
    temporaryIds?.map((badgeTemplateId) => ({
      badgeTemplateId,
      isDefault: badgeTemplateId === defaultTemporaryId,
    })) ?? [];
  const combined = [...permanentInputs, ...temporaryInputs];
  return combined.length ? combined : null;
};

export const getHidFargoConnectSectionInputs = ({
  proxyInstanceOptions,
  organizationOptions,
  locationOptions,
  printerOptions,
  badgeTemplateOptions,
  defaultBadgeTemplateOptions,
  temporaryBadgeTemplateOptions,
  defaultTemporaryBadgeTemplateOptions,
}: HidFargoConnectSectionInputsArgs): TypedFormInputs<
  EditJobsiteConfigurationFormData['hidFargoConnect']
> => ({
  jobsiteBadgeTemplates: {
    element: FormInputTypes.Select,
    label: 'Badge Templates',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3, 'sm:odin-col-start-1'],
    elementProps: {
      isMulti: true,
      clearToNull: true,
      options: badgeTemplateOptions ?? [],
    },
  },
  defaultJobsiteBadgeTemplate: {
    element: FormInputTypes.Select,
    label: 'Default Badge Template',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: {
      isMulti: false,
      clearToNull: true,
      options: defaultBadgeTemplateOptions ?? [],
      disabled: !defaultBadgeTemplateOptions?.length,
    },
  },
  jobsiteTemporaryBadgeTemplates: {
    element: FormInputTypes.Select,
    label: 'Temporary Badge Templates',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3, 'sm:odin-col-start-1'],
    elementProps: {
      isMulti: true,
      clearToNull: true,
      options: temporaryBadgeTemplateOptions ?? [],
    },
  },
  defaultJobsiteTemporaryBadgeTemplate: {
    element: FormInputTypes.Select,
    label: 'Default Temporary Badge Template',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
    elementProps: {
      isMulti: false,
      clearToNull: true,
      options: defaultTemporaryBadgeTemplateOptions ?? [],
      disabled: !defaultTemporaryBadgeTemplateOptions?.length,
    },
  },
  defaultJobsitePrinterId: {
    element: FormInputTypes.Field,
    label: 'Default Printer',
    layout: 'odin-hidden',
    elementProps: {
      disabled: true,
      icon: PrintIcon,
    },
  },
  jobsitePrinters: {
    element: FormInputTypes.Repeater,
    elementProps: {
      shouldTrackChanges: true,
      addItemText: 'Add Printer',
      getNewItem: (): Editable<JobsitePrinter> => ({
        id: getTempId(),
        proxyInstance: null,
        printerId: null,
        locationId: null,
        organizationId: null,
        changeType: null,
        isDefault: false,
      }),
      itemClassName: cn('odin-grid odin-grid-cols-6 odin-gap-3'),
      removeItemPosition: 'inline',
      removeItemClassName: 'odin-mt-6',
    },
    layout: 'odin-col-span-6',
    children: ensureNonUndefinedFields<
      TypedFormInputs<Editable<JobsitePrinter>>
    >({
      // RHF v6 (web-app) doesn't save `id` field without an input
      id: {
        element: FormInputTypes.Field,
        label: 'Id',
        layout: 'odin-hidden',
      },
      // RHF v6 (web-app) doesn't save `changeType` field without an input
      // use CustomInput because the Field component doesn't support undefined as `defaultValue`
      changeType: {
        element: FormInputTypes.CustomInput,
        label: '',
        elementProps: { customInput: React.forwardRef(() => null) },
        layout: 'odin-hidden',
      },
      isDefault: {
        element: FormInputTypes.Checkbox,
        label: 'Make Default',
        layout: [
          GridColSpan.SpanFull,
          'odin-flex',
          'odin-justify-end',
          'odin-mb-[-20px]',
        ],
      },
      proxyInstance: {
        element: FormInputTypes.Select,
        label: 'Proxy Instance',
        elementProps: {
          clearToNull: true,
          isClearable: false,
          options: proxyInstanceOptions,
          disabledFn: ({
            getItem,
          }: FormRepeaterArgs<Editable<JobsitePrinter>>): boolean => {
            return getItem()?.changeType !== ChangeType.Created;
          },
        },
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        validation: { required: true },
      },
      printerId: {
        element: FormInputTypes.Select,
        label: 'Printer',
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        elementProps: {
          clearToNull: true,
          isClearable: false,
          options: printerOptions ?? [],
          optionsFn: ({
            getItem,
            getFormValues,
          }: FormRepeaterArgs<
            Editable<JobsitePrinter>,
            EditJobsiteConfigurationFormData
          >): SelectOptionElement[] => {
            const selectedProxyInstanceId = getItem()?.proxyInstance?.value;
            const selectedPrinterIds = getFormValues()
              .hidFargoConnect?.jobsitePrinters?.map(
                (jp) => jp.printerId?.value,
              )
              .filter(Boolean);
            return (
              printerOptions.filter(
                (opt) =>
                  (!selectedProxyInstanceId ||
                    opt.entity?.proxyInstance === selectedProxyInstanceId) &&
                  !selectedPrinterIds?.includes(opt.value),
              ) ?? []
            );
          },
          disabledFn: ({
            getItem,
          }: FormRepeaterArgs<Editable<JobsitePrinter>>): boolean => {
            const item = getItem();
            return (
              item?.changeType !== ChangeType.Created ||
              !item?.proxyInstance?.value
            );
          },
          icon: PrintIcon,
        },
        validation: {
          required: true,
        },
      },
      organizationId: {
        element: FormInputTypes.Select,
        label: 'Organization',
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        elementProps: {
          isMulti: false,
          clearToNull: true,
          disabled: true,
          options: organizationOptions ?? [],
        },
      },
      locationId: {
        element: FormInputTypes.Select,
        label: 'Location',
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        elementProps: {
          isMulti: false,
          clearToNull: true,
          disabled: true,
          options: locationOptions ?? [],
        },
      },
    }),
  },
});

export const getHidFargoConnectSectionInputsHook =
  (
    args: HidFargoConnectSectionInputsHookArgs,
  ): UseInputs<EditJobsiteConfigurationFormData['hidFargoConnect']> =>
  (
    form: UseFormMethods<EditJobsiteConfigurationFormData['hidFargoConnect']>,
  ): TypedFormInputs<EditJobsiteConfigurationFormData['hidFargoConnect']> => {
    const { watch, setValue } = form;
    const {
      proxyInstanceOptions,
      organizationOptions,
      locationOptions,
      printerOptions,
      badgeTemplateOptions,
      temporaryBadgeTemplateOptions,
    } = args;
    const defaultJobsitePrinterId = watch(
      'hidFargoConnect.defaultJobsitePrinterId',
    ) as string;
    const currentBadgeSelections = watch(
      'hidFargoConnect.jobsiteBadgeTemplates',
    ) as BadgeTemplateOptionElement[];
    const defaultBadgeTemplateSelection = watch(
      'hidFargoConnect.defaultJobsiteBadgeTemplate',
    ) as BadgeTemplateOptionElement;
    const currentTemporaryBadgeSelections = watch(
      'hidFargoConnect.jobsiteTemporaryBadgeTemplates',
    ) as BadgeTemplateOptionElement[];
    const defaultTemporaryBadgeTemplateSelection = watch(
      'hidFargoConnect.defaultJobsiteTemporaryBadgeTemplate',
    ) as BadgeTemplateOptionElement;

    const jobsitePrinters = watch(
      'hidFargoConnect.jobsitePrinters',
    ) as EditJobsiteConfigurationFormData['hidFargoConnect']['jobsitePrinters'];

    const previousJobsitePrinters = React.useRef(jobsitePrinters);

    const setDefaultPrinterIfNecessary = React.useCallback(
      (
        currentJobsitePrinters: Editable<JobsitePrinter>[],
        prevJobsitePrinters: Editable<JobsitePrinter>[],
      ) => {
        const toRelevantIds = (
          acc: Set<string>,
          printer: Editable<JobsitePrinter>,
        ): Set<string> => {
          if (printer.isDefault && printer.changeType !== ChangeType.Removed)
            acc.add(printer.id);
          return acc;
        };

        const currentMarkedDefaultPrinters = currentJobsitePrinters.reduce(
          toRelevantIds,
          new Set(),
        );

        // three cases:
        // 1: no default marked - newDefaultPrinterId is undefined, nothing is updated
        // 2: one printer checked default - newDefaultPrinterId is defined, newDefaultPrinterId set as default in
        // hidFargoConnect.printerId
        // 3: multiple printers checked default - newDefaultPrinterId is defined, newDefaultPrinterId set as default in
        // hidFargoConnect.printerId, others default box unchecked by setValue
        const [newDefaultPrinterId] =
          currentMarkedDefaultPrinters.size > 1
            ? currentMarkedDefaultPrinters.difference(
                prevJobsitePrinters.reduce(toRelevantIds, new Set()),
              )
            : currentMarkedDefaultPrinters;

        const newDefaultPrinter = currentJobsitePrinters.find(
          (printer) => printer.id === newDefaultPrinterId,
        );
        const newDefaultJobsitePrinterId = newDefaultPrinter?.id;

        if (newDefaultJobsitePrinterId !== defaultJobsitePrinterId) {
          // if the default printer was changed, uncheck the default box for previous default printers
          currentJobsitePrinters.forEach((printer, index) => {
            if (printer.id !== newDefaultPrinterId && printer.isDefault) {
              setValue(
                `hidFargoConnect.jobsitePrinters[${index}].isDefault`,
                false,
                { shouldDirty: false },
              );
            }
          });

          setValue(
            'hidFargoConnect.defaultJobsitePrinterId',
            newDefaultJobsitePrinterId ?? '',
            {
              shouldDirty: true,
            },
          );
        }
      },
      [setValue, defaultJobsitePrinterId],
    );

    const deselectPrinterIdIfNecessary = React.useCallback(
      (
        currentJobsitePrinters: Editable<JobsitePrinter>[],
        prevJobsitePrinters: Editable<JobsitePrinter>[],
      ) => {
        // This function does the following:
        // 1. Put all printers (current and previous versions) into a single array.
        // 2. Filter null/undefined entries and entries with the 'internalKey' property. See comment explanation
        //    beginning on ~L381 about the 'internalKey' property.
        // 3. Put all printers in the combined array into a map of printer.id => list of printer objects.
        //    This results in an object containing one array per printer id, which will contain one printer
        //    if that printer was just added, and two if the printer already existed.
        // 4. Iterate over currentJobsitePrinters, so that we have the index of the printer we'd like to
        //    operate on; for each, get the paired printers and, if they do not have matching proxyInstance
        //    values (meaning the field was changed or nullified), null out the relevant fields.
        const printerPairs = currentJobsitePrinters
          .concat(prevJobsitePrinters)
          // The form "repeater" component uses internalKey to track newly added, unsaved rows.
          // When such rows are present, the repeater currently triggers onChange as it creates/
          // reorders items, which causes jobsitePrinters (and therefore the derived
          // assignedHidPrinterIds state) to be recomputed multiple times. If we update
          // assignedHidPrinterIds while those transient rows exist, we can inadvertently
          // overwrite in-progress user edits or cause unnecessary re-renders.
          // TODO: Investigate whether the repeater can avoid firing onChange when only
          // internalKey-related bookkeeping changes (e.g. when adding placeholder rows),
          // so that this guard (skipping setAssignedHidPrinterIds while internalKey rows
          // are present) is no longer needed.
          .filter((printer) => printer && !('internalKey' in printer))
          .reduce(
            (
              acc: Record<string, Editable<JobsitePrinter>[]>,
              printer: Editable<JobsitePrinter>,
            ) => {
              if (printer.id in acc) acc[printer.id].push(printer);
              else acc[printer.id] = [printer];
              return acc;
            },
            {},
          );
        currentJobsitePrinters.forEach(
          (printer: Editable<JobsitePrinter>, index) => {
            const printers = printerPairs[printer.id];
            if (
              printers &&
              printers.length === 2 &&
              printers[0].proxyInstance?.value !==
                printers[1].proxyInstance?.value
            ) {
              [
                `hidFargoConnect.jobsitePrinters[${index}].organizationId`,
                `hidFargoConnect.jobsitePrinters[${index}].printerId`,
                `hidFargoConnect.jobsitePrinters[${index}].locationId`,
              ].forEach((field) => {
                setValue(field, null, {
                  shouldDirty: false,
                });
              });
            }
          },
        );
      },
      [setValue],
    );

    React.useEffect(() => {
      if (deepEqual(previousJobsitePrinters.current, jobsitePrinters)) return;
      // to correctly respond to input re: only one default printer at a time, we
      // must handle here, before overwriting the state of previousJobsitePrinters
      setDefaultPrinterIfNecessary(
        jobsitePrinters,
        previousJobsitePrinters.current,
      );
      deselectPrinterIdIfNecessary(
        jobsitePrinters,
        previousJobsitePrinters.current,
      );
      previousJobsitePrinters.current = jobsitePrinters;

      // Populate organization and location fields for newly added jobsite printers
      jobsitePrinters?.forEach((createdPrinter, index) => {
        if (
          createdPrinter.changeType === ChangeType.Created &&
          createdPrinter.printerId
        ) {
          const { organizationId, locationId } =
            createdPrinter.printerId.entity ?? {};
          if (!createdPrinter.organizationId) {
            setValue(
              `hidFargoConnect.jobsitePrinters[${index}].organizationId`,
              organizationOptions.find((opt) => opt.value === organizationId) ??
                null,
              { shouldDirty: true },
            );
          }
          if (!createdPrinter.locationId) {
            setValue(
              `hidFargoConnect.jobsitePrinters[${index}].locationId`,
              locationOptions.find((opt) => opt.value === locationId) ?? null,
              {
                shouldDirty: true,
              },
            );
          }
        }
      });
    }, [jobsitePrinters]);

    const { defaultBadgeTemplateOptions, shouldResetDefaultBadgeTemplate } =
      React.useMemo(() => {
        const reset =
          !!defaultBadgeTemplateSelection &&
          !currentBadgeSelections?.some(
            (badge) => defaultBadgeTemplateSelection.value === badge.value,
          );
        return {
          defaultBadgeTemplateOptions: currentBadgeSelections ?? [],
          shouldResetDefaultBadgeTemplate: reset,
        };
      }, [currentBadgeSelections]);

    useDidUpdateEffect(() => {
      if (shouldResetDefaultBadgeTemplate) {
        setValue('hidFargoConnect.defaultJobsiteBadgeTemplate', null, {
          shouldDirty: true,
        });
      }
    }, [shouldResetDefaultBadgeTemplate, defaultBadgeTemplateSelection]);

    const {
      defaultTemporaryBadgeTemplateOptions,
      shouldResetDefaultTemporaryBadgeTemplate,
    } = React.useMemo(() => {
      const reset =
        !!defaultTemporaryBadgeTemplateSelection &&
        !currentTemporaryBadgeSelections?.some(
          (badge) =>
            defaultTemporaryBadgeTemplateSelection.value === badge.value,
        );
      return {
        defaultTemporaryBadgeTemplateOptions:
          currentTemporaryBadgeSelections ?? [],
        shouldResetDefaultTemporaryBadgeTemplate: reset,
      };
    }, [
      currentTemporaryBadgeSelections,
      defaultTemporaryBadgeTemplateSelection,
    ]);

    useDidUpdateEffect(() => {
      if (shouldResetDefaultTemporaryBadgeTemplate) {
        setValue('hidFargoConnect.defaultJobsiteTemporaryBadgeTemplate', null, {
          shouldDirty: true,
        });
      }
    }, [shouldResetDefaultTemporaryBadgeTemplate]);

    return React.useMemo(() => {
      return getHidFargoConnectSectionInputs({
        proxyInstanceOptions,
        organizationOptions,
        locationOptions,
        printerOptions,
        badgeTemplateOptions,
        defaultBadgeTemplateOptions,
        temporaryBadgeTemplateOptions,
        defaultTemporaryBadgeTemplateOptions,
      });
    }, [
      organizationOptions,
      locationOptions,
      printerOptions,
      badgeTemplateOptions,
      defaultBadgeTemplateOptions,
      temporaryBadgeTemplateOptions,
      defaultTemporaryBadgeTemplateOptions,
    ]);
  };

export const getHidFargoConnectSectionDefaultValues = (
  jobsite: Jobsite,
  hidProxyInstanceOptions: SelectOptionElement<HidProxyInstance>[],
  hidOrganizationOptions: HidFargoSelectOptionElement[],
  hidLocationOptions: HidFargoSelectOptionElement[],
  hidPrinterOptions: HidFargoSelectOptionElement[],
  badgeTemplateOptions: BadgeTemplateOptionElement[],
  temporaryBadgeTemplateOptions: BadgeTemplateOptionElement[],
): EditJobsiteConfigurationFormData['hidFargoConnect'] => {
  const { jobsiteBadgeTemplates, defaultJobsitePrinterId } = jobsite ?? {};
  const currentBadgeTemplates =
    jobsiteBadgeTemplates?.edges?.map(({ node }) => node) ?? [];

  const currentPermanentBadgeTemplates = currentBadgeTemplates.filter(
    (jbt) => !jbt.badgeTemplate.isForTempBadge,
  );
  const currentTemporaryBadgeTemplates = currentBadgeTemplates.filter(
    (jbt) => jbt.badgeTemplate.isForTempBadge,
  );

  const currentDefaultJobsiteBadgeTemplate =
    currentPermanentBadgeTemplates.find((jbt) => jbt.isDefault) ?? null;
  const defaultJobsiteBadgeTemplate =
    badgeTemplateOptions.find(
      (opt) =>
        opt.badgeTemplate.id ===
        currentDefaultJobsiteBadgeTemplate?.badgeTemplate.id,
    ) ?? null;

  const currentDefaultTemporaryBadgeTemplate =
    currentTemporaryBadgeTemplates.find((jbt) => jbt.isDefault) ?? null;
  const defaultJobsiteTemporaryBadgeTemplate =
    temporaryBadgeTemplateOptions.find(
      (opt) =>
        opt.badgeTemplate.id ===
        currentDefaultTemporaryBadgeTemplate?.badgeTemplate.id,
    ) ?? null;

  const jobsitePrinters = jobsite?.jobsitePrinters?.edges
    .map(
      ({
        node,
      }): EditJobsiteConfigurationFormData['hidFargoConnect']['jobsitePrinters'][number] => {
        return {
          id: node.id,
          changeType: null,
          proxyInstance:
            hidProxyInstanceOptions.find(
              (opt) => opt.value === node.hidProxyInstance,
            ) ?? null,
          printerId:
            hidPrinterOptions.find((opt) => opt.value === node.hidPrinterId) ??
            null,
          organizationId:
            hidOrganizationOptions.find(
              (opt) => opt.value === node.fargoOrganizationId,
            ) ?? null,
          locationId:
            hidLocationOptions.find(
              (opt) => opt.value === node.fargoLocationId,
            ) ?? null,
          isDefault: node.id === jobsite.defaultJobsitePrinterId,
        };
      },
    )
    .sort((a, b) =>
      (a.printerId?.label ?? '').localeCompare(b.printerId?.label ?? ''),
    );

  return {
    defaultJobsitePrinterId: defaultJobsitePrinterId ?? '',
    jobsiteBadgeTemplates:
      badgeTemplateOptions.filter((opt) =>
        currentPermanentBadgeTemplates.some(
          (jbt) => jbt.badgeTemplate.id === opt.badgeTemplate.id,
        ),
      ) ?? null,
    defaultJobsiteBadgeTemplate,
    jobsiteTemporaryBadgeTemplates:
      temporaryBadgeTemplateOptions.filter((opt) =>
        currentTemporaryBadgeTemplates.some(
          (jbt) => jbt.badgeTemplate.id === opt.badgeTemplate.id,
        ),
      ) ?? null,
    defaultJobsiteTemporaryBadgeTemplate,
    ...(jobsitePrinters?.length ? { jobsitePrinters } : undefined),
  };
};

type HidFargoConnectSectionUpdateInput = Required<
  Pick<
    JobsiteUpdateInput,
    'defaultJobsitePrinterId' | 'jobsiteBadgeTemplateInputs' | 'jobsitePrinters'
  >
>;

export const getHidFargoConnectSectionUpdateInput = (
  hidFargoConnect: EditJobsiteConfigurationFormData['hidFargoConnect'],
  dirtyFields: DeepMap<
    EditJobsiteConfigurationFormData['hidFargoConnect'],
    true
  >,
): HidFargoConnectSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    hidFargoConnect,
    dirtyFields,
  );
  const updatedDefaultBadgeTemplateId = getUpdateInputValue(
    'defaultJobsiteBadgeTemplate',
  );
  const updatedJobsiteBadgeTemplateIds = getUpdateInputValue(
    'jobsiteBadgeTemplates',
  );
  const updatedDefaultTemporaryBadgeTemplateId = getUpdateInputValue(
    'defaultJobsiteTemporaryBadgeTemplate',
  );
  const updatedJobsiteTemporaryBadgeTemplateIds = getUpdateInputValue(
    'jobsiteTemporaryBadgeTemplates',
  );

  let jobsiteBadgeTemplateInputs: HidFargoConnectSectionUpdateInput['jobsiteBadgeTemplateInputs'];
  if (
    updatedDefaultBadgeTemplateId !== undefined ||
    updatedJobsiteBadgeTemplateIds !== undefined ||
    updatedDefaultTemporaryBadgeTemplateId !== undefined ||
    updatedJobsiteTemporaryBadgeTemplateIds !== undefined
  ) {
    jobsiteBadgeTemplateInputs = buildJobsiteBadgeTemplateInputs({
      permanentIds:
        updatedJobsiteBadgeTemplateIds ??
        hidFargoConnect.jobsiteBadgeTemplates?.map((i) => i.value),
      defaultPermanentId:
        updatedDefaultBadgeTemplateId ??
        hidFargoConnect.defaultJobsiteBadgeTemplate?.value,
      temporaryIds:
        updatedJobsiteTemporaryBadgeTemplateIds ??
        hidFargoConnect.jobsiteTemporaryBadgeTemplates?.map((i) => i.value),
      defaultTemporaryId:
        updatedDefaultTemporaryBadgeTemplateId ??
        hidFargoConnect.defaultJobsiteTemporaryBadgeTemplate?.value,
    });
  }

  const jobsitePrintersInput = getUpdateInputValue('jobsitePrinters')
    ?.filter((jp) => jp.changeType)
    .map(
      (jp): HidFargoConnectSectionUpdateInput['jobsitePrinters'][number] => ({
        id: jp.id,
        hidPrinterId: jp.printerId && jp.printerId.value,
        fargoOrganizationId: jp.organizationId && jp.organizationId.value,
        fargoLocationId: jp.locationId && jp.locationId.value,
        changeType: jp.changeType,
        hidProxyInstance: jp.proxyInstance.value,
      }),
    );

  return {
    defaultJobsitePrinterId: getUpdateInputValue('defaultJobsitePrinterId'),
    jobsiteBadgeTemplateInputs,
    jobsitePrinters:
      ensureNonUndefinedFields<
        HidFargoConnectSectionUpdateInput['jobsitePrinters']
      >(jobsitePrintersInput),
  };
};
